import { z } from 'zod';
import { PageQuerySchema } from './common';

export const ENROLLMENT_STATUSES = ['en_cours', 'admis', 'ajourne', 'abandon'] as const;
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number];
export const GENDERS = ['F', 'M'] as const;

const opt = (s: z.ZodString) => s.nullable().optional();
const text = (max: number) => opt(z.string().trim().max(max));

/** Fields editable on a student record (Informations tab), validated identically in forms and API. */
export const StudentFieldsSchema = z.object({
  firstName: z.string().trim().min(1, 'students.errors.required').max(80),
  lastName: z.string().trim().min(1, 'students.errors.required').max(80),
  firstNameAr: text(80),
  lastNameAr: text(80),
  gender: z.enum(GENDERS).nullable().optional(),
  cin: opt(
    z
      .string()
      .trim()
      .regex(/^\d{8}$/, 'students.errors.cin'),
  ),
  birthDate: z.iso.date('students.errors.date').nullable().optional(),
  birthPlace: text(80),
  nationality: text(60),
  email: z
    .email('students.errors.email')
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  phone: opt(
    z
      .string()
      .trim()
      .regex(/^\+?[0-9 ]{8,20}$/, 'students.errors.phone'),
  ),
  address: text(200),
  city: text(80),
  bacSection: text(80),
  bacSession: text(40),
  bacYear: z.number().int().min(1990).max(2100).nullable().optional(),
  bacAverage: z
    .number()
    .min(0, 'students.errors.average')
    .max(20, 'students.errors.average')
    .nullable()
    .optional(),
  bacSchool: text(120),
  guardianName: text(120),
  guardianRelation: text(40),
  guardianPhone: opt(
    z
      .string()
      .trim()
      .regex(/^\+?[0-9 ]{8,20}$/, 'students.errors.phone'),
  ),
  guardianProfession: text(80),
});
export type StudentFields = z.input<typeof StudentFieldsSchema>;

export const UpdateStudentSchema = StudentFieldsSchema.partial();
export type UpdateStudent = z.input<typeof UpdateStudentSchema>;

export const EnrollmentInputSchema = z.object({
  academicYearId: z.uuid(),
  levelId: z.uuid('students.errors.level'),
  groupId: z.uuid().nullable().optional(),
});
export type EnrollmentInput = z.infer<typeof EnrollmentInputSchema>;

export const CreateStudentSchema = StudentFieldsSchema.extend({ enrollment: EnrollmentInputSchema });
export type CreateStudent = z.input<typeof CreateStudentSchema>;

export const StudentListQuerySchema = PageQuerySchema.extend({
  yearId: z.uuid(),
  programId: z.uuid().optional(),
  levelId: z.uuid().optional(),
  groupId: z.uuid().optional(),
  status: z.enum([...ENROLLMENT_STATUSES, 'none']).optional(),
});
export type StudentListQuery = z.input<typeof StudentListQuerySchema>;

export const StudentRowSchema = z.object({
  id: z.uuid(),
  matricule: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  firstNameAr: z.string().nullable(),
  lastNameAr: z.string().nullable(),
  cin: z.string().nullable(),
  programCode: z.string().nullable(),
  programName: z.string().nullable(),
  levelCode: z.string().nullable(),
  groupName: z.string().nullable(),
  enrollmentId: z.uuid().nullable(),
  enrollmentStatus: z.enum(ENROLLMENT_STATUSES).nullable(),
});
export type StudentRow = z.infer<typeof StudentRowSchema>;

export const EnrollmentSchema = z.object({
  id: z.uuid(),
  academicYearId: z.uuid(),
  yearLabel: z.string(),
  yearStatus: z.string(),
  levelId: z.uuid(),
  levelCode: z.string(),
  levelLabel: z.string(),
  programId: z.uuid(),
  programName: z.string(),
  programNameAr: z.string().nullable(),
  programCycle: z.string(),
  groupId: z.uuid().nullable(),
  groupName: z.string().nullable(),
  curriculumId: z.uuid().nullable(),
  enrolledOn: z.string(),
  status: z.enum(ENROLLMENT_STATUSES),
  creditsAcquired: z.number().int().nullable(),
  creditsTarget: z.number().int(),
});
export type Enrollment = z.infer<typeof EnrollmentSchema>;

export const StudentDetailSchema = z.object({
  id: z.uuid(),
  matricule: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  firstNameAr: z.string().nullable(),
  lastNameAr: z.string().nullable(),
  gender: z.enum(GENDERS).nullable(),
  cin: z.string().nullable(),
  birthDate: z.string().nullable(),
  birthPlace: z.string().nullable(),
  nationality: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  bacSection: z.string().nullable(),
  bacSession: z.string().nullable(),
  bacYear: z.number().int().nullable(),
  bacAverage: z.number().nullable(),
  bacSchool: z.string().nullable(),
  guardianName: z.string().nullable(),
  guardianRelation: z.string().nullable(),
  guardianPhone: z.string().nullable(),
  guardianProfession: z.string().nullable(),
  enrollments: z.array(EnrollmentSchema),
  /** Credits acquired over all closed years, and the programme total (e.g. 150 / 180). */
  creditsAcquired: z.number().int(),
  creditsTotal: z.number().int(),
});
export type StudentDetail = z.infer<typeof StudentDetailSchema>;

export const UpdateEnrollmentSchema = z.object({
  groupId: z.uuid().nullable().optional(),
  status: z.enum(ENROLLMENT_STATUSES).optional(),
});
export type UpdateEnrollment = z.infer<typeof UpdateEnrollmentSchema>;

export const BulkGroupChangeSchema = z.object({
  enrollmentIds: z.array(z.uuid()).min(1).max(500),
  groupId: z.uuid(),
});
export type BulkGroupChange = z.infer<typeof BulkGroupChangeSchema>;

export const HistoryEventSchema = z.object({
  id: z.uuid(),
  action: z.string(),
  at: z.string(),
  actorName: z.string().nullable(),
  /** Action-specific values used to build the i18n message (e.g. year, level, fields). */
  data: z.record(z.string(), z.union([z.string(), z.number(), z.array(z.string())])),
});
export type HistoryEvent = z.infer<typeof HistoryEventSchema>;
